import styled from '@emotion/styled';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import Button from '@shared/components/core/button/CoreButton';
import DeleteIconButton from '@shared/components/core/button/DeleteIconButton';
import { CardBorder } from '@shared/components/core/card/CommonCardStyles';
import LegacyCard from '@shared/components/core/card/LegacyCard';
import TextInputWithAdornment from '@shared/components/core/field/TextInputWithAdornment';
import { ContentLayout, ContentTitle } from '@shared/components/core/text/ContentTypography';
import { FieldLabel } from '@shared/components/core/text/FormsTypography';
import { BodyLarge, BodySmall } from '@shared/components/core/text/Typography';
import { useEditProfileStore } from '@shared/hooks/state/EditProfileContainer';
import { useProfilesStore } from '@shared/hooks/state/ProfilesContainer';
import DeviceProfileShareModel from '@shared/models/deviceProfiles/DeviceProfileShareModel';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

export default function ProfileSharePageContent() {
    const { t } = useTranslation();
    const { profileId } = useParams();

    const [shareLink, setShareLink] = useState<string>('');
    const [hasExpiration, setHasExpiration] = useState<boolean>(false);
    const [isSingleUse, setIsSingleUse] = useState<boolean>(false);
    const [expirationDate, setExpirationDate] = useState<Dayjs | null>(dayjs().add(1, 'day'));
    const [existingShares, setExistingShares] = useState<DeviceProfileShareModel[]>([]);
    const createLink = async () => {
        const result = await useProfilesStore
            .getState()
            .createShareLink(profileId, isSingleUse, hasExpiration ? expirationDate : null);
        if (result?.resultType == 'Ok' && result.data?.accessCode) {
            const shareLink = `https://share.cephable.com/profileshare/copy/${result.data.accessCode}`;
            setShareLink(shareLink);
            navigator.clipboard.writeText(shareLink);
        }
    };
    const loadShares = async () => {
        const result = await useProfilesStore.getState().getShares(profileId);
        if (result?.resultType == 'Ok') {
            setExistingShares(result.data?.filter((s) => s.isDisabled != true) ?? []);
        }
    };
    const deleteShare = async (id: string) => {
        const result = await useProfilesStore.getState().deleteShare(id);
        if (result?.resultType == 'Ok') {
            loadShares();
        }
    };
    useEffect(() => {
        loadShares();
    }, []);

    // listens to ctrl+c or cmd+c
    useHotkeys(['mod+c'], () => {
        navigator.clipboard.writeText(JSON.stringify(useEditProfileStore.getState().getUserDeviceProfileFromState()));
    });
    return (
        <>
            <CardBorder>
                <ContentLayout>
                    {shareLink ? (
                        <TextInputWithAdornment
                            value={shareLink}
                            readOnly
                            onClick={() => navigator.clipboard.writeText(shareLink)}
                            inputLabel={t('common:profile.share.link')}
                            actionLabel={t('common:profile.share.copy')}
                        />
                    ) : (
                        <>
                            <FormControl fullWidth>
                                <FieldLabel>{t('common:profile.share.usage')}</FieldLabel>
                                <RadioGroup
                                    name="isSingleUse"
                                    defaultValue="right"
                                    onChange={(e) => setIsSingleUse(e.target.value == 'true')}
                                    value={isSingleUse ? 'true' : 'false'}>
                                    <FormControlLabel
                                        value="true"
                                        control={<Radio />}
                                        label={t('common:profile.share.expiration.once')}
                                    />
                                    <FormControlLabel
                                        value="false"
                                        control={<Radio />}
                                        label={t('common:profile.share.expiration.unlimited')}
                                    />
                                </RadioGroup>

                                <FieldLabel>{t('common:profile.share.expiration.title')}</FieldLabel>
                                <RadioGroup
                                    name="hasExpiration"
                                    defaultValue="right"
                                    onChange={(e) => setHasExpiration(e.target.value == 'true')}
                                    value={hasExpiration ? 'true' : 'false'}>
                                    <FormControlLabel
                                        value="false"
                                        control={<Radio />}
                                        label={t('common:profile.share.expiration.no')}
                                    />
                                    <FormControlLabel
                                        value="true"
                                        control={<Radio />}
                                        label={t('common:profile.share.expiration.dateDescription')}
                                    />
                                </RadioGroup>
                                {hasExpiration && (
                                    <DatePicker
                                        label={t('common:profile.share.expiration.date')}
                                        value={expirationDate}
                                        onChange={(e) => setExpirationDate(e)}
                                    />
                                )}
                                <Button onClick={createLink}>{t('common:profile.share.add')}</Button>
                            </FormControl>
                        </>
                    )}
                </ContentLayout>
            </CardBorder>

            <ContentLayout>
                <ContentTitle>{t('common:profile.share.manage')}</ContentTitle>
                {existingShares?.length > 0 ? (
                    existingShares.map((share, index) => (
                        <ShareCard key={index}>
                            <BodyLarge>
                                {t('common:profile.share.created', 'created', {
                                    value: dayjs(share.createdDate)?.format('dddd MMMM DD, YYYY'),
                                })}
                            </BodyLarge>
                            <DeleteIconButton onClick={() => deleteShare(share.id)}>Remove</DeleteIconButton>
                        </ShareCard>
                    ))
                ) : (
                    <BodySmall>{t('common:profile.share.empty')}</BodySmall>
                )}
            </ContentLayout>
        </>
    );
}

const ShareCard = styled(LegacyCard)`
    margin: 8px;
    padding: 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
    background: ${(props) => props.theme.color.accentCard};
    > button {
        margin-left: auto;
        margin-right: 0;
    }
`;
